<template>
    <v-tooltip
        :bottom="position === 'bottom'"
        :top="position === 'top'"
        :left="position === 'left'"
        :right="position === 'right'"
        :disabled="!shouldEnableTooltip"
        max-width="320"
        :content-class="'disqualification-tooltip-content ' + (isDarkTheme ? 'dark-mode' : 'light-mode')"
    >
        <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="disqualification-activator">
                <slot></slot>
            </span>
        </template>

        <div class="disqualification-tooltip" v-if="shouldEnableTooltip">
            <div class="tooltip-header">
                <i class="fa-solid fa-circle-exclamation mr-2"></i>
                <label>{{ $t('disqualification.title') }}</label>
            </div>

            <div class="tooltip-body">
                <!-- Loading state -->
                <div v-if="loading" class="tooltip-loading">
                    <v-progress-circular indeterminate size="20" width="2" color="white"></v-progress-circular>
                    <label class="ml-2">Loading...</label>
                </div>

                <!-- No data found -->
                <div v-else-if="fetched && !hasDisqualificationReason" class="tooltip-no-data">
                    <label>{{ $t('disqualification.noData') || 'No disqualification details available' }}</label>
                </div>

                <template v-else-if="hasDisqualificationReason">
                    <!-- Rule Information -->
                    <div class="tooltip-row" v-if="effectiveReason.ruleTitle">
                        <label class="tooltip-label">{{ $t('disqualification.rule') }}:</label>
                        <label class="tooltip-value">{{ effectiveReason.ruleTitle }}</label>
                    </div>

                    <!-- Manual / admin disqualification reason (participant) -->
                    <div class="tooltip-row tooltip-row--custom-reason" v-if="displayCustomReason">
                        <label class="tooltip-label">{{ $t('disqualification.customReason') }}:</label>
                        <label class="tooltip-value">{{ displayCustomReason }}</label>
                    </div>

                    <!-- Breached Value -->
                    <div class="tooltip-row" v-if="effectiveReason.breachedValue !== undefined">
                        <label class="tooltip-label">{{ $t('disqualification.breachedValue') }}:</label>
                        <label class="tooltip-value breached">{{ formatValue(effectiveReason.breachedValue, effectiveReason.dataType) }}</label>
                        <label class="tooltip-threshold" v-if="effectiveReason.threshold !== undefined">
                            ({{ $t('disqualification.threshold') }}: {{ formatValue(effectiveReason.threshold, effectiveReason.dataType) }})
                        </label>
                    </div>

                    <!-- Date/Time -->
                    <div class="tooltip-row" v-if="effectiveReason.occurredAt">
                        <label class="tooltip-label">{{ $t('disqualification.dateTime') }}:</label>
                        <label class="tooltip-value">{{ formatDateTime(effectiveReason.occurredAt) }}</label>
                    </div>

                    <!-- Trade ID Reference -->
                    <div class="tooltip-row trade-reference" v-if="effectiveReason.tradeId">
                        <label class="tooltip-label">{{ $t('disqualification.tradeId') }}:</label>
                        <label class="tooltip-value">#{{ effectiveReason.tradeId }}</label>
                    </div>
                </template>
            </div>
        </div>
    </v-tooltip>
</template>

<script>
import axios from 'axios';

export default {
    name: 'DisqualificationTooltip',
    props: {
        /**
         * Participant ID to fetch disqualification reason
         */
        participantId: {
            type: Number,
            default: null
        },
        /**
         * Disqualification reason object (optional - if provided, skip API call)
         */
        reason: {
            type: Object,
            default: () => null
        },
        /**
         * Tooltip position: 'bottom', 'top', 'left', 'right'
         */
        position: {
            type: String,
            default: 'bottom',
            validator: (value) => ['bottom', 'top', 'left', 'right'].includes(value)
        }
    },
    data() {
        return {
            fetchedReason: null,
            loading: false,
            fetched: false
        };
    },
    computed: {
        effectiveReason() {
            // Use passed reason prop if available, otherwise use fetched data
            return this.reason || this.fetchedReason;
        },
        hasDisqualificationReason() {
            const r = this.effectiveReason;
            if (!r) return false;
            const hasCustom = this.displayCustomReason.length > 0;
            return !!(r.ruleTitle || r.breachedAt || r.tradeId || hasCustom);
        },
        displayCustomReason() {
            const r = this.effectiveReason;
            if (!r) return '';
            const v = r.customReason != null ? r.customReason : r.disqualificationReason;
            if (v == null || v === '') return '';
            return String(v).trim();
        },
        shouldEnableTooltip() {
            // Enable tooltip if we have a participantId (will fetch) or already have reason data
            return this.participantId || this.hasDisqualificationReason;
        },
        isDarkTheme() {
            return this.$vuetify?.theme?.dark || false;
        }
    },
    watch: {
        participantId: {
            immediate: true,
            handler(newId) {
                if (newId && !this.reason) {
                    this.fetchDisqualificationReason();
                }
            }
        }
    },
    methods: {
        async fetchDisqualificationReason() {
            if (!this.participantId || this.fetched) return;

            this.loading = true;
            try {
                const basePath =  process.env.VUE_APP_BACKEND_URL || this.$store.state.backend;
                const token = this.$store.state.token || 'Bearer ' + this.$store.getters['broker/authToken'];
                const response = await axios.get(
                    `${basePath}/ext/tournament-participant/${this.participantId}/disqualified`,
                    { headers: { 'Authorization': token } }
                );

                if (response.status < 400 && response.data && response.data.length > 0) {
                    // Map API response to expected format
                    const apiReason = response.data[0];
                    this.fetchedReason = {
                        ruleTitle: apiReason.ruleTitle,
                        breachedValue: apiReason.breachedValue,
                        threshold: apiReason.ruleValue,
                        occurredAt: apiReason.breachedAt,
                        tradeId: apiReason.tradeId,
                        dataType: apiReason.dataType,
                        customReason: apiReason.disqualificationReason != null
                            ? apiReason.disqualificationReason
                            : apiReason.customReason,
                    };
                }
            } catch (error) {
                console.error('Error fetching disqualification reason:', error);
            } finally {
                this.loading = false;
                this.fetched = true;
            }
        },
        formatDateTime(isoString) {
            if (!isoString) return '';
            try {
                const date = new Date(isoString);
                return new Intl.DateTimeFormat(this.$i18n.locale, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                }).format(date);
            } catch {
                return isoString;
            }
        },
        formatValue(value, dataType) {
            if (value === null || value === undefined) return '-';

            // Handle formatting based on dataType
            switch (dataType) {
                case 'PERCENTAGE':
                    // Value comes as decimal (0.3 = 30%), convert to percentage
                    if (typeof value === 'number') {
                        return `${(value * 100).toFixed(2)}%`;
                    }
                    return `${value}%`;

                case 'INTEGER':
                    return Math.round(value).toString();

                case 'FLOAT':
                    if (typeof value === 'number') {
                        return value.toFixed(2);
                    }
                    return value.toString();

                case 'BOOLEAN':
                    return value ? 'Yes' : 'No';

                case 'STRING':
                    return value.toString();

                default:
                    if (value === null || value === undefined) return '-';
                    return value;
            }
        }
    }
}
</script>

<style lang="scss">
.disqualification-tooltip-content {
    border-radius: 8px !important;
    padding: 0 !important;
    opacity: 1 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;

    &.light-mode {
        background-color: #ffffff !important;
    }

    &.dark-mode {
        background-color: var(--v-mainWhite-base) !important;
    }
}

.disqualification-tooltip {
    padding: 12px 16px;
    font-family: 'Avenir', sans-serif;

    .tooltip-header {
        display: flex;
        align-items: center;
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 10px;
        color: #e53935;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        padding-bottom: 8px;

        i {
            font-size: 16px;
            color: #e53935 !important;
        }
    }

    .tooltip-body {
            .tooltip-row {
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            margin-bottom: 6px;
            font-size: 13px;
            line-height: 1.4;

            &.tooltip-row--custom-reason {
                align-items: flex-start;

                .tooltip-value {
                    flex: 1;
                    min-width: 0;
                    white-space: pre-wrap;
                    word-break: break-word;
                    font-weight: 400;
                }
            }

            &:last-child {
                margin-bottom: 0;
            }

            .tooltip-label {
                opacity: 0.7;
                margin-right: 6px;
                white-space: nowrap;
            }

            .tooltip-value {
                font-weight: 500;

                &.breached {
                    color: #e53935 !important;
                    font-weight: 600;
                }
            }

            .tooltip-threshold {
                opacity: 0.6;
                font-size: 12px;
                margin-left: 6px;
            }

            &.trade-reference {
                margin-top: 8px;
                padding-top: 8px;
                border-top: 1px solid rgba(0, 0, 0, 0.1);

                .tooltip-value {
                    color: #1976d2 !important;
                    font-family: monospace;
                }
            }
        }
    }
}

.dark-mode .disqualification-tooltip {
    .tooltip-header {
        border-bottom-color: rgba(255, 255, 255, 0.1);
    }

    .tooltip-body .tooltip-row.trade-reference {
        border-top-color: rgba(255, 255, 255, 0.1);
    }
}

.disqualification-activator {
    display: inline-flex;
    //cursor: help;
}

.tooltip-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
    font-size: 13px;
    opacity: 0.7;
}

.tooltip-no-data {
    text-align: center;
    padding: 8px 0;
    font-size: 13px;
    font-style: italic;
    opacity: 0.6;
}
</style>
